import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface ToastItem {
  id: number;
  message: string;
  variant: 'default' | 'error' | 'success';
}

interface ToastCtx {
  push: (message: string, variant?: ToastItem['variant']) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const push = useCallback<ToastCtx['push']>((message, variant = 'default') => {
    const id = ++idRef.current;
    setList((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => setList((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  return (
    <Ctx.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
        {list.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto min-w-[240px] rounded-md border px-4 py-3 text-sm shadow-md',
              t.variant === 'error' && 'border-destructive/40 bg-destructive text-destructive-foreground',
              t.variant === 'success' && 'border-emerald-500/40 bg-emerald-600 text-white',
              t.variant === 'default' && 'bg-card text-card-foreground'
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
